import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Briefcase,
  Cpu,
  LayoutGrid,
  type LucideIcon,
  Megaphone,
  Scale,
  Sparkles,
  Sprout,
  Store,
  Users,
} from 'lucide-react';

import { api } from '../../lib/api';
import { IS_EMBEDDED } from '../../lib/embed';
import { useAuth } from '../../lib/auth/AuthProvider';
import { isCompanyAdmin } from '../../lib/team';
import { useVerticals } from '../../lib/catalog';
import { Spinner } from '../ui';
import { t } from '../../lib/i18n';
import { trackRegistrationVertical } from '../../lib/conversion';

/** Mapa nombre de ícono (columna `vertical.icon`) → componente lucide. */
const VERTICAL_ICONS: Record<string, LucideIcon> = {
  store: Store,
  scale: Scale,
  megaphone: Megaphone,
  cpu: Cpu,
  briefcase: Briefcase,
  users: Users,
  sprout: Sprout,
};

const SKIP_KEY = 'tembiapo:onboarding-vertical-skipped';

/**
 * Onboarding post-registro: elegir el RUBRO (P2). El registro ya no lo pide; acá,
 * en el primer ingreso a la app, el admin elige su rubro y a partir de ahí ve las
 * plantillas de su industria. Se dispara el evento de embudo RegistroPaso1Rubro.
 *
 * Se muestra sólo al COMPANY_ADMIN cuando el backend reporta `vertical_pending`.
 * Se puede posponer (por sesión); vuelve a aparecer hasta que elija.
 */
export function VerticalOnboarding() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canChoose = isCompanyAdmin(user?.role);
  const [skipped, setSkipped] = useState(() => {
    try {
      return sessionStorage.getItem(SKIP_KEY) === '1';
    } catch {
      return false;
    }
  });

  const statusQ = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => api.get<{ vertical_pending: boolean }>('/onboarding/status'),
    enabled: canChoose,
    staleTime: 60_000,
  });

  const verticals = useVerticals();

  const setVertical = useMutation({
    mutationFn: (verticalId: string) =>
      api.post<{ project_id?: string | null }>('/onboarding/vertical', { vertical_id: verticalId }),
    onSuccess: (data, verticalId) => {
      const name = verticals.data?.find((v) => v.id === verticalId)?.name;
      trackRegistrationVertical(name);
      // Refrescamos estado + plantillas + proyectos (ahora hay un proyecto de ejemplo).
      qc.invalidateQueries({ queryKey: ['onboarding-status'] });
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      // Activación: si se creó el proyecto de ejemplo, llevamos al usuario directo
      // a su tablero con datos (el "ajá"). Si no, cae al cierre normal del modal.
      if (data?.project_id) {
        navigate(`/proyectos/${data.project_id}`);
      }
    },
  });

  // No aparece dentro de las islas web de la app nativa (IS_EMBEDDED).
  const open = !IS_EMBEDDED && canChoose && !skipped && statusQ.data?.vertical_pending === true;
  if (!open) return null;

  const skip = () => {
    try {
      sessionStorage.setItem(SKIP_KEY, '1');
    } catch {
      /* modo privado: degradamos a memoria */
    }
    setSkipped(true);
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && skip()}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay-in fixed inset-0 z-[60] bg-ink/50 backdrop-blur-[2px]" />
        <Dialog.Content
          aria-describedby={undefined}
          // Paso obligatorio: no se cierra por clic afuera ni Escape (evita que el
          // tour u otro overlay le roben el foco y lo descarten). Sólo se cierra al
          // elegir un rubro o con el botón explícito "Elegir después".
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className="modal-in fixed left-1/2 top-1/2 z-[60] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-token border border-line bg-surface p-6 shadow-lift focus:outline-none sm:p-8"
        >
          <div className="mb-5 space-y-1.5 text-center">
            <span className="mx-auto mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-brand/10">
              <Sparkles size={20} className="text-brand" />
            </span>
            <Dialog.Title className="font-display text-xl font-bold text-ink">
              {t('onboarding.vertical.title')}
            </Dialog.Title>
            <p className="text-sm text-muted">{t('onboarding.vertical.subtitle')}</p>
          </div>

          {verticals.isLoading && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}

          {verticals.data && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {verticals.data.map((v) => {
                const Icon = VERTICAL_ICONS[v.icon ?? ''] ?? LayoutGrid;
                return (
                  <button
                    key={v.id}
                    type="button"
                    disabled={setVertical.isPending}
                    onClick={() => setVertical.mutate(v.id)}
                    className="flex flex-col items-center gap-2 rounded-token border border-line bg-surface px-3 py-4 text-center text-sm text-ink transition-colors hover:border-brand hover:bg-brand/5 disabled:opacity-60"
                  >
                    <Icon aria-hidden size={22} className="text-brand-2" />
                    <span className="font-medium">{v.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={skip}
              disabled={setVertical.isPending}
              className="text-xs text-muted underline-offset-2 hover:text-ink hover:underline"
            >
              {t('onboarding.vertical.skip')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
